import clsx from 'clsx'
import React, { FC, useCallback, useContext, useState } from 'react'

import SvgPlus from '@Assets/SVG/Plus'
import looksStyles from '@Blocks/LookList/v2.module.scss'
import { useScrollToTop } from '@Blocks/ProductList/useScrollToTop'
import styles from '@Blocks/ProductList/v2.module.scss'
import { BlockContainer } from '@Components/BlockContainer'
import { CategoryFacetsWithChips } from '@Components/Facet/Display/CategoryMenu/CategoryFilters'
import { SVGIcon } from '@Components/Icons/SVGIcon'
import { Link } from '@Components/Link'
import {
  NativeModal,
  NativeModalBody,
  NativeModalFooter,
  NativeModalHeader,
  useNativeModalActions,
} from '@Components/Modal/NativeModal/NativeModal'
import { Pagination, PaginationArrows, PaginationShowingText } from '@Components/Pagination'
import { Tiles } from '@Components/Tiles'
import { TilesCardLook } from '@Components/Tiles/Card/Look'
import { useTileSettings } from '@Components/Tiles/settings'
import { calculateSizes } from '@Components/Tiles/sizes'
import { isEventDesktop } from '@Domains/Navigation/Features/Menu/Menu'
import { LookListBlock as LookListBlockGraphQL, PagePresentation, PageType } from '@Generated/graphql'
import { ProductSearchContext } from '@Globals/Algolia/Context'
import { LookSearchContext } from '@Globals/Algolia/Looks/Context'
import { LOOKS_HITS_PER_PAGE } from '@Globals/Algolia/Looks/Shared'
import { useCMSModalOpen } from '@Globals/CMS/Modal/Hook'
import { Image } from '@Globals/Image'
import { useTranslation } from '@Globals/Translations'

export interface LookListBlockV2Props {
  data: LookListBlockGraphQL
  id?: string
}

export const LookListBlockV2: FC<LookListBlockV2Props> = ({ data, id }) => {
  const { t } = useTranslation()
  const [appliedFiltersOnMobile, setAppliedFiltersOnMobile] = useState(false)
  const { scrollToTop, wrapperRef } = useScrollToTop()
  const filterModalActions = useNativeModalActions()
  const { count, currentHits, page, pages } = useContext(ProductSearchContext)
  const { looks } = useContext(LookSearchContext)
  const openModal = useCMSModalOpen()
  // looks are v2-only: the authored version is ignored (see Components/Looks)
  const tileSettings = { ...useTileSettings(data.settings), version: '2' }
  // the desktop filter sidebar takes its rail off the tile grid
  const sizesHint = calculateSizes(tileSettings, looks.length, { facetRail: true })

  const onFilterChangeMobile = useCallback(() => {
    setAppliedFiltersOnMobile(true)
    scrollToTop()
  }, [scrollToTop])

  return (
    <BlockContainer
      appearance={{ ...data.appearance || {} }}
      className={looksStyles.blockContainer}
      common={{
        ...data.common,
        position: data.common?.position || 'top',
        text: data.common?.text || undefined,
        title: data.common?.title || data.title || '',
        titleElement: data.common?.titleElement || 'h2',
      }}
      id={id}
      presentation={PagePresentation.Full}
    >
      <div className={clsx(styles.productListBlock, looksStyles.chrome)}>
        <p className={`${styles.numberOfProducts} ${styles.numberOfProductsMobile}`}>
          {t('common:looks_x_looks', '{{arg1}} looks', { arg1: currentHits })}
        </p>
        <div className={styles.actions} ref={wrapperRef}>
          <div className={styles.sortingAndFilters}>
            <div className={styles.filterWrapper}>
              <button
                className={styles.filterButton}
                id="looks-filter"
                onClick={(e) => {
                  if (!isEventDesktop(e)) {
                    filterModalActions.current?.open()
                  }
                }}
                type="button"
              >
                <div className={styles.filterTitleFilters}>
                  <p className={styles.filterTitleText}>
                    {t('common:FILTERS')}
                    {count > 0 && <span>{` (${count})`}</span>}
                  </p>
                  <SVGIcon icon={SvgPlus} size="20px" strokeColor="var(--neutral-900)" />
                </div>
              </button>
            </div>

            <p className={`${styles.numberOfProducts} ${styles.numberOfProductsTablet}`}>
              {t('common:looks_x_looks', '{{arg1}} looks', { arg1: currentHits })}
            </p>
          </div>
          <div className={styles.productGrid}>
            <Tiles settings={tileSettings}>
              {looks.map((look, index) => (
                <TilesCardLook
                  key={look.id}
                  ambassadorHandle={look.ambassadorHandle || undefined}
                  cardAspectRatio={tileSettings.aspectRatio}
                  image={look.asset as Image}
                  onClick={tileSettings.action === 'drawer' || !look.url
                    ? (e) => {
                      e.preventDefault()
                      e.stopPropagation()

                      openModal(PageType.Look, look.id!, 'inherit', PagePresentation.Sidebar)
                    }
                    : undefined}
                  pins={[]}
                  presentation={PagePresentation.Full}
                  priority={index < 4}
                  render={tileSettings.action === 'page' && look.url
                    ? props => (
                      <Link href={look.url!} {...props} />
                    )
                    : undefined}
                  renderButtonAsText={Boolean(tileSettings.action === 'page' && look.url)}
                  sizes={sizesHint}
                />
              ))}
            </Tiles>
            {pages > 1 && (
              <Pagination>
                <PaginationArrows onClick={scrollToTop} pages={pages} />
                <PaginationShowingText
                  currentHits={currentHits}
                  defaults="Showing {{from}} to {{to}} of {{total}} looks"
                  i18nKey="common:pagination_showing_from_to_of_total_looks"
                  page={page || 1}
                  perPage={LOOKS_HITS_PER_PAGE}
                />
              </Pagination>
            )}
          </div>
          <div className={styles.filtersOnDesktop}>
            <CategoryFacetsWithChips
              key="looks_filter"
              className={styles.filterList}
              onFilterChange={scrollToTop}
            />
          </div>
          <div />
        </div>
        <NativeModal
          actionRef={filterModalActions}
          alignment="left"
          backdrop
          onClose={() => {
            filterModalActions.current?.close()
            if (appliedFiltersOnMobile) {
              scrollToTop()
              setAppliedFiltersOnMobile(false)
            }
          }}
        >
          <NativeModalHeader>
            {t('common:FILTERS')}
          </NativeModalHeader>
          <NativeModalBody>
            <div className={styles.filterModal}>
              <div className={styles.filterModalFilters}>
                <CategoryFacetsWithChips
                  key="looks_filter"
                  className={styles.filterList}
                  mobile
                  onFilterChange={onFilterChangeMobile}
                />
              </div>
            </div>
          </NativeModalBody>
          <NativeModalFooter className={styles.footerButton}>
            <button
              className={styles.filterButtonMobil}
              onClick={(e) => {
                if (!isEventDesktop(e)) {
                  scrollToTop()
                  filterModalActions.current?.close()
                }
              }}
              type="button"
            >
              <p>
                {t('products:SHOW_RESULTS')}
                {` (${currentHits})`}
              </p>
            </button>
          </NativeModalFooter>
        </NativeModal>
      </div>
    </BlockContainer>
  )
}
